import * as adminApi from '@/api/admin';
import { usePermissionsStore } from './permissionsStore';

const PERMISSIONS_CACHE_KEY = 'user_permissions';
const REQUEST_TIMEOUT = 1000; // 1秒超时

/**
 * 从 localStorage 获取缓存的权限
 */
const getCachedPermissions = (): Record<string, any> | null => {
  try {
    const cached = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Failed to parse cached permissions', error);
  }
  return null;
};

/**
 * 将权限保存到 localStorage
 */
const cachePermissions = (permissions: Record<string, any>) => {
  try {
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(permissions));
  } catch (error) {
    console.error('Failed to cache permissions', error);
  }
};

/**
 * 带超时的权限请求
 */
const fetchPermissionsWithTimeout = async (timeout: number): Promise<Record<string, any> | null> => {
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), timeout);
  });

  const fetchPromise = adminApi.GetUserPermissions().then(res => {
    if (res?.ok) {
      return res.data?.permissions?.system || {};
    }
    return null;
  }).catch(() => null);

  return Promise.race([fetchPromise, timeoutPromise]);
};

/**
 * 通用权限获取方法
 * 
 * 逻辑：
 * 1. 尝试从服务器获取最新权限（最多等待1秒）
 * 2. 如果成功，更新全局状态和localStorage缓存
 * 3. 如果超时，使用全局状态中的权限
 * 4. 如果请求失败且全局状态为空，使用localStorage中的缓存
 * 
 * @param forceRefresh 是否强制刷新（忽略全局状态）
 * @returns 返回权限对象
 */
export const fetchUserPermissions = async (forceRefresh: boolean = false): Promise<Record<string, any>> => {
  const store = usePermissionsStore.getState();
  
  // 如果不强制刷新且全局状态已有权限且不在加载中，直接返回
  if (!forceRefresh && !store.isLoading && Object.keys(store.permissions).length > 0) {
    return store.permissions;
  }

  // 标记开始加载
  store.setLoading(true);

  try {
    // 尝试从服务器获取权限（带超时）
    const permissions = await fetchPermissionsWithTimeout(REQUEST_TIMEOUT);

    if (permissions && Object.keys(permissions).length > 0) {
      // 请求成功，更新全局状态和缓存
      store.setPermissions(permissions);
      cachePermissions(permissions);
      return permissions;
    }

    // 请求失败或超时，使用全局状态中的权限
    if (Object.keys(store.permissions).length > 0) {
      store.setLoading(false);
      return store.permissions;
    }

    // 全局状态为空，尝试使用localStorage中的缓存
    const cachedPermissions = getCachedPermissions();
    if (cachedPermissions && Object.keys(cachedPermissions).length > 0) {
      store.setPermissions(cachedPermissions);
      return cachedPermissions;
    }

    // 所有方式都失败，返回空对象
    store.setLoading(false);
    return {};
  } catch (error) {
    console.error('Failed to fetch permissions', error);
    
    // 异常情况，使用全局状态或缓存
    if (Object.keys(store.permissions).length > 0) {
      store.setLoading(false);
      return store.permissions;
    }

    const cachedPermissions = getCachedPermissions();
    if (cachedPermissions) {
      store.setPermissions(cachedPermissions);
      return cachedPermissions;
    }

    store.setLoading(false);
    return {};
  }
};

/**
 * 初始化权限（页面加载时调用）
 */
export const initializePermissions = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) {
    usePermissionsStore.getState().clearPermissions();
    return;
  }

  await fetchUserPermissions(true);
};

/**
 * 检查是否有特定权限
 */
export const hasPermission = (permission: string): boolean => {
  const permissions = usePermissionsStore.getState().getPermissions();
  return permissions[permission] === true;
};

/**
 * 清除权限缓存
 */
export const clearPermissionsCache = (): void => {
  localStorage.removeItem(PERMISSIONS_CACHE_KEY);
  usePermissionsStore.getState().clearPermissions();
};
